/** One owner for startup, selection changes, and scheduled weather refresh. */
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useLocationStore } from "./location.store";
import { useWeatherStore } from "./weather.store";
import { useSettingsStore } from "./settings.store";

export type SessionPhase = "idle" | "locating" | "ready";

export const useWeatherSessionStore = defineStore("weather-session", () => {
  const locations = useLocationStore();
  const weather = useWeatherStore();
  const settings = useSettingsStore();
  const phase = ref<SessionPhase>("idle");
  const activeLocation = computed(() => locations.displayLocation);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopSelection: (() => void) | undefined;
  let stopSummaries: (() => void) | undefined;
  let stopInterval: (() => void) | undefined;
  let generation = 0;

  /** Refresh saved-place summaries through their single weather-store owner. */
  function refreshSummaries() {
    return weather.fetchLocationSummaries(
      locations.savedLocations.map((location) => ({
        id: location.id,
        location,
      })),
    );
  }

  /** Refresh the active location and saved-place summaries together. */
  async function refresh(force = false) {
    await Promise.all([
      weather.fetchWeather(activeLocation.value, force),
      refreshSummaries(),
    ]);
  }

  /** Schedule one weather refresh timer while the session is active. */
  function schedule() {
    clearTimeout(timer);
    if (phase.value === "idle" || settings.autoRefreshInterval <= 0) return;
    const run = generation;
    timer = setTimeout(async () => {
      // Avoid polling an invisible app; visibility resumes through the same owner.
      if (!document.hidden) await refresh(true);
      if (run === generation) schedule();
    }, settings.autoRefreshInterval * 60_000);
  }

  /** Resume refreshes when a hidden app becomes visible. */
  function onVisible() {
    if (
      !document.hidden &&
      settings.autoRefreshInterval > 0 &&
      phase.value === "ready"
    ) {
      void refresh();
      schedule();
    }
  }

  /** Initialize location selection and install the session's request watchers. */
  async function start() {
    if (phase.value !== "idle") return;
    const run = ++generation;
    const initialSelection = locations.selectionVersion;
    phase.value = "locating";
    weather.setLoading(true);
    stopSelection = watch(
      [
        () => activeLocation.value.lat,
        () => activeLocation.value.lng,
        () => settings.useMockData,
        () => settings.mockWeatherCondition,
        () => settings.usePrivateOpenMeteo,
      ],
      () => {
        if (
          phase.value === "locating" &&
          locations.selectionVersion === initialSelection
        )
          return;
        phase.value = "ready";
        void refresh();
      },
    );
    stopSummaries = watch(
      () => [
        locations.savedLocations
          .map((place) => `${place.id}:${place.lat}:${place.lng}`)
          .join("|"),
        settings.useMockData,
      ],
      () => {
        void refreshSummaries();
      },
      { immediate: true },
    );
    stopInterval = watch(() => settings.autoRefreshInterval, schedule);
    document.addEventListener("visibilitychange", onVisible);
    await locations.initializeSelection();
    if (run !== generation) return;
    phase.value = "ready";
    await refresh();
    if (run === generation) schedule();
  }

  /** Stop timers and watchers so an inactive session cannot fetch weather. */
  function stop() {
    generation++;
    phase.value = "idle";
    clearTimeout(timer);
    stopSelection?.();
    stopInterval?.();
    stopSummaries?.();
    document.removeEventListener("visibilitychange", onVisible);
  }

  return { phase, activeLocation, refresh, start, stop };
});
